import { Link } from 'react-router-dom'
import Logo from '@/components/shared/Logo'

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>

            <nav aria-label="Breadcrumb" className="hidden sm:block">
              <ol className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <li>
                  <Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">
                    Home
                  </Link>
                </li>
                <li aria-hidden>•</li>
                <li className="text-gray-700 dark:text-gray-200 font-medium">Terms of Service</li>
              </ol>
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pt-24">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">Terms of Service</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Effective Date: December 2025</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                On this page
              </p>
              <nav className="space-y-2 text-sm">
                <a href="#acceptance" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  1. Acceptance of Terms
                </a>
                <a href="#service" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  2. Description of Service
                </a>
                <a href="#accounts" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  3. User Accounts
                </a>
                <a href="#acceptable-use" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  4. Acceptable Use
                </a>
                <a href="#payment" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  5. Payment Terms
                </a>
                <a href="#ip" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  6. Intellectual Property
                </a>
                <a href="#liability" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  7. Limitation of Liability
                </a>
                <a href="#termination" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  8. Termination
                </a>
                <a href="#contact" className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                  9. Contact Information
                </a>
              </nav>
            </div>
          </aside>

          {/* Terms Content */}
          <section className="lg:col-span-9">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 sm:p-8 space-y-8">
              <section id="acceptance">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  1. Acceptance of Terms
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  By accessing or using AsiliFlow's services, you agree to be bound by these Terms of
                  Service. If you do not agree to these terms, please do not use our services.
                </p>
              </section>

              <section id="service">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  2. Description of Service
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  AsiliFlow provides a cloud-based platform for healthcare agency operations management,
                  including scheduling, shift tracking, authorization management, and compliance reporting.
                  Our services include caregiver scheduling, client management, shift documentation,
                  and audit trail features.
                </p>
              </section>

              <section id="accounts">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  3. User Accounts
                </h2>
                <p className="mb-3 text-gray-700 dark:text-gray-300">To use our services, you must:</p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Create an account with accurate information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Be responsible for all activities under your account</li>
                </ul>
              </section>

              <section id="acceptable-use">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  4. Acceptable Use
                </h2>
                <p className="mb-3 text-gray-700 dark:text-gray-300">You agree not to:</p>
                <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon intellectual property rights</li>
                  <li>Upload false or misleading information</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with the proper functioning of our services</li>
                </ul>
              </section>

              <section id="payment">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  5. Payment Terms
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  Subscription fees are billed in advance on a monthly or annual basis. All payments
                  are non-refundable except as required by law. We reserve the right to modify pricing
                  with 30 days advance notice.
                </p>
              </section>

              <section id="ip">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  6. Intellectual Property
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  All content, features, and functionality of AsiliFlow are owned by Asili LLC
                  and are protected by international copyright, trademark, and other intellectual
                  property laws.
                </p>
              </section>

              <section id="liability">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  7. Limitation of Liability
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  AsiliFlow shall not be liable for any indirect, incidental, special, consequential,
                  or punitive damages resulting from your use or inability to use our services, even if
                  we have been advised of the possibility of such damages.
                </p>
              </section>

              <section id="termination">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  8. Termination
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  Either party may terminate these terms at any time. Upon termination, your access to
                  our services will cease immediately. Provisions that should survive termination will
                  remain in effect.
                </p>
              </section>

              <section id="contact">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  9. Contact Information
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  For questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-3 text-gray-700 dark:text-gray-300">
                  <p>
                    Email:{' '}
                    <a
                      href="mailto:support@getasili.com"
                      className="text-primary-600 hover:text-primary-700 underline underline-offset-2"
                    >
                      support@getasili.com
                    </a>
                  </p>
                </div>
              </section>

              {/* Back to top */}
              <div className="pt-2">
                <a
                  href="#"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 rounded"
                >
                  ↑ Back to top
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-sm text-gray-500 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
              Terms of Service
            </Link>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-300">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
